import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { ArtworkCard } from "@/components/ArtworkCard";
import { CollectionCard } from "@/components/CollectionCard";
import { ArrowRight } from "lucide-react";

export const HomePage = () => {
  const [featuredArtworks, setFeaturedArtworks] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [artworksRes, collectionsRes] = await Promise.all([
          axios.get(`${API}/artworks?featured=true`),
          axios.get(`${API}/collections`),
        ]);
        setFeaturedArtworks(artworksRes.data);
        setCollections(collectionsRes.data);
      } catch (e) {
        console.error("Error fetching data:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-[70vh] md:h-[85vh] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1618913001600-4284b12e1623?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Colorful abstract artwork"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/30" />
        
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <p className="text-sm md:text-base uppercase tracking-[0.3em] text-white/80 mb-4 animate-fade-in">
            A Family Collection
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-7xl text-white font-medium tracking-tight leading-tight max-w-4xl animate-fade-in stagger-1">
            Welcome to<br />Kavya's Art
          </h1>
          <p className="mt-6 text-white/80 max-w-xl text-base md:text-lg animate-fade-in stagger-2">
            Discover unique digital artwork created with love by our family, celebrating the beauty of each passing season.
          </p>
          <div className="flex gap-4 mt-8 animate-fade-in stagger-3">
            <Link to="/shop">
              <Button 
                className="rounded-none px-8 py-6 bg-white text-black hover:bg-white/90 uppercase tracking-widest text-xs font-bold"
                data-testid="hero-shop-btn"
              >
                Shop Collection
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Artworks */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 max-w-screen-2xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
          <div>
            <p className="text-sm uppercase tracking-widest text-muted-foreground mb-2">Curated Selection</p>
            <h2 className="font-serif text-3xl md:text-5xl font-normal tracking-tight">Featured Artwork</h2>
          </div>
          <Link 
            to="/shop" 
            className="mt-4 md:mt-0 flex items-center gap-2 text-sm uppercase tracking-widest hover:text-primary/70 transition-colors"
            data-testid="view-all-link"
          >
            View All <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
            {[1, 2, 3].map((i) => (
              <div key={i} className="aspect-[3/4] bg-muted animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
            {featuredArtworks.slice(0, 6).map((artwork, index) => (
              <ArtworkCard key={artwork.id} artwork={artwork} index={index} />
            ))}
          </div>
        )}
      </section>

      {/* Collections */}
      <section id="collections" className="py-24 md:py-32 bg-muted/30">
        <div className="px-6 md:px-12 lg:px-24 max-w-screen-2xl mx-auto mb-12">
          <p className="text-sm uppercase tracking-widest text-muted-foreground mb-2">Browse By Season</p>
          <h2 className="font-serif text-3xl md:text-5xl font-normal tracking-tight">Our Collections</h2>
        </div>

        <div className="px-6 md:px-12">
          {loading ? (
            <div className="flex gap-6 overflow-x-auto pb-8 no-scrollbar">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="min-w-[280px] md:min-w-[350px] aspect-[4/5] bg-muted animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="flex overflow-x-auto snap-x snap-mandatory gap-6 pb-8 no-scrollbar">
              {collections.map((collection, index) => (
                <CollectionCard key={collection.id} collection={collection} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-screen-2xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
          <div className="order-2 lg:order-1">
            <p className="text-sm uppercase tracking-widest text-muted-foreground mb-4">Our Story</p>
            <h2 className="font-serif text-3xl md:text-5xl font-normal tracking-tight mb-6">
              Created With Love
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Welcome to Kavya's Art, a special collection of digital artwork created by Kavya (and assisted by mom). 
              Each piece is a labor of love, capturing the unique beauty and emotions of every season.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              From the fresh blooms of spring to the quiet serenity of winter, her artwork tells the story 
              of the world around her through her eyes. We hope these pieces bring as much joy to your 
              space as they did to us in creating them.
            </p>
            <Link to="/shop">
              <Button 
                className="rounded-none px-8 py-3 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest text-xs font-bold"
                data-testid="about-shop-btn"
              >
                Explore Our Art
              </Button>
            </Link>
          </div>
          <div className="order-1 lg:order-2">
            <img
              src="https://customer-assets.emergentagent.com/job_creatives-store/artifacts/58zdzz34_1000048420.jpg"
              alt="Kavya, the young artist behind The Seasonal Gallery"
              className="w-full aspect-[4/5] object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-primary text-primary-foreground">
        <div className="max-w-2xl mx-auto text-center px-6">
          <h2 className="font-serif text-3xl md:text-5xl font-normal tracking-tight mb-6">
            Start Your Collection
          </h2>
          <p className="text-primary-foreground/80 mb-8">
            Digital downloads delivered instantly to your inbox. No waiting, no shipping - 
            just beautiful art ready to print and display.
          </p>
          <Link to="/shop">
            <Button 
              className="rounded-none px-8 py-3 bg-primary-foreground text-primary hover:bg-primary-foreground/90 uppercase tracking-widest text-xs font-bold"
              data-testid="cta-shop-btn"
            >
              Shop Now
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
